import { gql } from '~/techstyle-shared/react-graphql';

import { MEMBERSHIP_STATUS } from './constants';

// Reference: https://studio.apollographql.com/graph/fashionos/variant/dev/schema/reference/objects/ProductsByGroupCodeResult?query=productlisting
// https://studio.apollographql.com/graph/fashionos/variant/dev/schema/reference/inputs/ProductsByGroupCodeQuery

const PRODUCTS_BY_GROUP_CODE_QUERY = gql`
  fragment SwatchFields on GroupCodeProductSwatch {
    label
    url
    isSelf
    productId
    type
  }

  fragment PricingDetailsFields on CustomerProductPrice {
    masterProductId
    dateExpected
    vip
    final
    sales
    retail
    outlet
    retailStoreOutlet
    bogoDetails {
      promoTypeId
      promoPrice
      promoLabel
      promoCode
      displayOnPdp
    }
    discountDetails {
      promoTypeId
      promoPrice
      promoLabel
      promoCode
      displayOnPdp
    }
    markdownPercentOff
    basePrice
    pricingDatasource {
      version
      sourceUrl
    }
  }

  query ProductsByGroupCode(
    $productQuery: ProductsByGroupCodeQuery
    $membershipStatus: Int
  ) {
    productsByGroupCode(productQuery: $productQuery) {
      total
      productsByGroupCode {
        groupCode
        total
        products {
          color
          dateExpected
          groupCode
          itemNumber
          label
          masterProductId
          membershipLevelFlags {
            leadOnly
            eliteOnly
            vipOnly
          }
          permalink
          swatch {
            ...SwatchFields
          }
          tags {
            id
            label
          }
          isWarehouseProduct
          availableSizes {
            sizeLabel
            sizes
          }
          imageServiceUrl
          imagesSummary {
            counts {
              defaultDetails {
                imageVersions
              }
              plusDetails {
                imageVersions
              }
            }
          }
          availableQuantity {
            anyProfile
            ca
            master
            preorderAnyProfile
            preorderMasterDefault
            preorderMaster
          }
          pricing(membershipStatus: $membershipStatus) {
            ...PricingDetailsFields
          }
        }
      }
    }
  }
`;

/**
 * Function to fetch related products data by group code using GraphQL via Apollo client.
 *
 * Note: productsByGroupCode query filters out OutOfStock products if they are Limited, it will
 * not filter out OutOfStock Core products.
 *
 * @param {Object} apolloClient - Apollo client instance
 * @param {Array<Object>} groupCodeObjects - array of objects { groupCode: string, masterProductIds: Array<string> }
 * @param {boolean} isVip - Flag to indicate if the user is a VIP member
 * @param {boolean} isWarehouseProduct - Flag to include warehouse products
 * @param {string} membershipLevelGroup - Membership level group filter (values like "vip", "elite", "lead")
 * @param {Array<number>} excludeTagIds - Array of tag IDs to exclude from results
 * @returns {Promise} - Promise that resolves to the related product data
 */
const getGqlRelatedProductDataByGroupCode = ({
  apolloClient,
  groupCodeObjects,
  isVip,
  isWarehouseProduct,
  membershipLevelGroup,
  excludeTagIds,
}) => {
  /* membershipLevelGroup is used by productsByGroupCode
   * membershipStatus is used by productPricing, without it related products will
   * have pricing for the default membership status (ie leads).
   */
  const relatedProductsVariables = {
    productQuery: {
      groupCodes: groupCodeObjects,
      isWarehouseProduct,
      membershipLevelGroup: {
        [membershipLevelGroup]: true,
      },
      ...(excludeTagIds?.length && {
        tagId: { nin: excludeTagIds },
      }),
      limit: 7,
      excludeOutOfStockSwatchType: 'Limited',
    },
    membershipStatus: isVip
      ? MEMBERSHIP_STATUS.VIP
      : MEMBERSHIP_STATUS.PROSPECTIVE,
  };

  return apolloClient.query({
    context: {
      clientName: 'supergraph',
      headers: {
        'x-datasource-version': 'pricingServicev3',
      },
    },
    query: PRODUCTS_BY_GROUP_CODE_QUERY,
    variables: relatedProductsVariables,
  });
};

export default getGqlRelatedProductDataByGroupCode;
